import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export const useOptimizedNavigation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const navigateWithPrefetch = useCallback((path: string, prefetchKeys?: string[]) => {
    // Prefetch data for the target page
    if (prefetchKeys) {
      prefetchKeys.forEach(key => {
        queryClient.prefetchQuery({
          queryKey: [key],
          staleTime: 2 * 60 * 1000, // 2 minutes
        });
      });
    }

    // Navigate immediately without waiting for prefetch
    navigate(path);
  }, [navigate, queryClient]);

  const cleanupProjectQueries = useCallback((projectId?: string) => {
    const keys = [
      'project-detail',
      'specs-data',
      'diary-data',
      'diary-specs',
      'diary-photos',
      'diary-pins',
      'pin-image-counts',
    ];

    if (projectId) {
      // Scoped cleanup - use predicate to match all queries with prefix [key, projectId]
      keys.forEach(key => {
        const matchPredicate = (query: { queryKey: readonly unknown[] }) => {
          const qKey = query.queryKey;
          return Array.isArray(qKey) && qKey[0] === key && qKey[1] === projectId;
        };
        queryClient.cancelQueries({ predicate: matchPredicate });
        // Only invalidate INACTIVE queries - preserve cache for back navigation
        queryClient.invalidateQueries({ 
          predicate: (query) => matchPredicate(query) && !query.isActive()
        });
      });
      if (import.meta.env.DEV) {
        console.debug('🔄 Invalidated inactive queries for project:', projectId);
      }
    } else {
      // Global cleanup - match all queries starting with key
      keys.forEach(key => {
        const matchPredicate = (query: { queryKey: readonly unknown[] }) => {
          const qKey = query.queryKey;
          return Array.isArray(qKey) && qKey[0] === key;
        };
        queryClient.cancelQueries({ predicate: matchPredicate });
        // Only invalidate INACTIVE queries - preserve cache for back navigation
        queryClient.invalidateQueries({ 
          predicate: (query) => matchPredicate(query) && !query.isActive()
        });
      });
      if (import.meta.env.DEV) {
        console.debug('🔄 Invalidated all inactive project queries (global)');
      }
    }
  }, [queryClient]);

  const navigateFromProject = useCallback((path: string) => {
    // Cleanup project-specific queries first
    cleanupProjectQueries();
    // Navigate immediately
    navigate(path);
  }, [navigate, cleanupProjectQueries]);

  return {
    navigateWithPrefetch,
    cleanupProjectQueries,
    navigateFromProject
  };
};